import { auth } from '@/app/config/firebase'

interface RequestOptions extends RequestInit {
  skipAuth?: boolean
}

class HttpClient {
  private async getAuthToken(): Promise<string | null> {
    try {
      if (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_TOKEN) {
        return import.meta.env.VITE_DEV_AUTH_TOKEN as string
      }
      const user = auth.currentUser
      if (!user) {
        return null
      }
      const token = await user.getIdToken()
      return token
    } catch (error) {
      console.error('Failed to get auth token:', error)
      return null
    }
  }

  private async prepareHeaders(options?: RequestOptions): Promise<HeadersInit> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Merge any existing headers
    if (options?.headers) {
      Object.assign(headers, options.headers)
    }

    if (!options?.skipAuth) {
      const token = await this.getAuthToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    return headers
  }

  async fetch(url: string, options?: RequestOptions): Promise<Response> {
    const headers = await this.prepareHeaders(options)

    return fetch(url, {
      ...options,
      headers,
    })
  }

  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    const response = await this.fetch(url, {
      ...options,
      method: 'GET',
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json() as Promise<T>
  }

  async post<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const response = await this.fetch(url, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json() as Promise<T>
  }

  async put<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const response = await this.fetch(url, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json() as Promise<T>
  }

  async delete<T>(url: string, options?: RequestOptions): Promise<T | null> {
    const response = await this.fetch(url, {
      ...options,
      method: 'DELETE',
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    if (response.status === 204) {
      return null
    }

    return response.json() as Promise<T>
  }
}

export const httpClient = new HttpClient()
